/**
 * RegisterPaymentPage (billing) — Registrar Pagamento (POST /billing/payments).
 *
 * 3 etapas:
 *  1) Dados do Recebimento — tudo do payload fora de invoices[]: data (+hora opcional), valor,
 *     tipo de pagamento e campos dependentes, fonte/sub-rogação/aval a devolver, pagador com
 *     busca no person service e comprovante [placeholder]. O responsável não é campo de tela:
 *     é o usuário logado. Os blocos de tipo de pagamento e de pagador são compartilhados com a
 *     baixa/liquidação de título (components/molecules/payment).
 *  2) Selecionar Contratos — busca no contracts-management + seleção de operações.
 *  3) Selecionar Faturas — invoices OPEN das operações; por fatura: value/total_payment/
 *     cancel_invoice/uncollect. Barra de alocação até 100% do valor informado (submete só em 100%).
 *
 * Comprovante: o PDF sobe antes do envio e o nome canônico volta do backend em file_name_receipt.
 */
import { useCallback, useContext, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import {
    FaFileInvoiceDollar, FaChevronDown, FaChevronUp, FaCheckSquare, FaRegSquare,
    FaCloudUploadAlt, FaTimes,
} from 'react-icons/fa';
import PaymentTypeFields, { PaymentTypeValue, emptyPaymentTypeValue, assetDationTotal } from '../../molecules/payment/PaymentTypeFields';
import PayerFields, { PayerValue, emptyPayer, onlyDigits } from '../../molecules/payment/PayerFields';
import { buildPaymentBlock, newPaymentId } from '../../molecules/payment/paymentPayload';
import PaymentDateInput, { toIsoInstant } from '../../molecules/payment/PaymentDateInput';
import { amountHint } from '../../molecules/payment/paymentHints';
import HintBadge from '../../molecules/payment/HintBadge';
import { PaymentType, PaymentMethod } from '../../../constants/payment';
import { AuthContext } from '../../../services/Auth/AuthContext';
import {
    listOpenChargesByOperation, processPayment, markInvoices, PaymentInvoiceItem,
    uploadPaymentReceipt,
    ProcessPaymentPayload, MarkInvoiceItem, PaymentActionPromise, PaymentRow,
} from './services/billingApi';
import PendingPaymentPicker from '../../molecules/payment/PendingPaymentPicker';
import PaymentInfoModal from './modals/PaymentInfoModal';
import { FilterBuilder, AppliedFilter, serializeFilters } from '../../molecules/FilterBuilder';
import { operationsFilterSchema } from '../operations/constants/operationFilterSchema';
import { listOperations, SettlementOperation } from '../settlement/services/settlementApi';
import { formatTaxId } from '../../../utils/taxIdUtils';
import {
    LiquidationMode, MODE_TO_TOTAL_PAYMENT, MODE_LABELS, MODE_ACTIONS, WATERFALL_HINT,
    PAYMENT_MODES_BY_SCENARIO, modeOf, clampMode,
} from './constants/liquidationModes';
import { CHARGE_SCENARIO_LABELS } from './constants/chargeFilterSchema';
import PageForm from '../../templates/PageForm';

/**
 * Os dois atos que esta tela sabe fazer.
 *
 * `REGISTER` é o ato ÚNICO: registra o dinheiro, escolhe o que ele paga e liquida na mesma
 * submissão. Está **deprecated** — sem conferência no meio — e não é mais oferecido no menu.
 *
 * `IDENTIFY` é o passo 2 do ato desmembrado: o dinheiro já entrou (passo 1, financeiro) e aqui a
 * cobrança apenas diz QUAIS faturas ele paga. A liquidação vem na aprovação (passo 3).
 *
 * Um componente e não dois arquivos: as etapas 2 e 3 são idênticas nos dois atos, e duplicá-las
 * faria uma correção feita aqui continuar errada lá. Voltar ao fluxo antigo é trocar a rota.
 */
export type PageMode = 'REGISTER' | 'IDENTIFY';

const STEPS_BY_MODE: Record<PageMode, readonly [string, string, string]> = {
    // "Dados do Recebimento" seria mentira no modo IDENTIFY: não se informa recebimento nenhum,
    // escolhe-se um que já existe
    REGISTER: ['Dados do Recebimento', 'Selecionar Contratos', 'Selecionar Faturas'],
    IDENTIFY: ['Selecionar Pagamento', 'Selecionar Contratos', 'Selecionar Faturas'],
};

const PAGE_TITLE: Record<PageMode, string> = {
    REGISTER: 'Registrar Recebimento',
    IDENTIFY: 'Identificação de Pagamento',
};

const PAGE_ROUTE: Record<PageMode, string> = {
    REGISTER: '/billing/register-payment',
    IDENTIFY: '/billing/payment-identification',
};

/**
 * A promessa que o rascunho por TÍTULO carrega. Sempre `_ALL`: aqui a linha é um título inteiro,
 * não uma fatura escolhida — o usuário decidiu o que fazer com a COBRANÇA daquele título, e as
 * irmãs de uma quebra em frações continuariam cobrando dívida já resolvida se o efeito fosse só
 * na fatura de origem.
 */
export const draftPromise = (
    cancelInvoice: boolean, uncollect: boolean,
): PaymentActionPromise => {
    // cancelar vence descobrar: cancelar já para de cobrar, então o par é redundante, não ambíguo
    if (cancelInvoice) return 'CANCEL_ALL';
    if (uncollect) return 'UNCOLLECT_ALL';
    return 'NOTHING';
};

/** Stepper das três etapas — vive no controlSlot do PageForm, grudado no cabeçalho. */
const Stepper: React.FC<{ step: number; steps: readonly string[] }> = ({ step, steps }) => (
    <div className="flex items-center">
        {steps.map((s, idx) => (
            <div key={s} className="flex items-center flex-1">
                <div className={`flex items-center gap-2 ${idx <= step ? 'text-blue-600 font-semibold' : 'text-gray-400'}`}>
                    <span className={`w-7 h-7 rounded-full flex items-center justify-center text-sm ${idx < step ? 'bg-green-500 text-white' : idx === step ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-500'}`}>
                        {idx < step ? '✓' : idx + 1}
                    </span>
                    <span className="text-sm">{s}</span>
                </div>
                {idx < steps.length - 1 && <div className={`flex-1 h-0.5 mx-4 ${idx < step ? 'bg-green-500' : 'bg-gray-200'}`} />}
            </div>
        ))}
    </div>
);

const fmt = (n: number) => n.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
// "2.500.000,00" | "2500000.00" → number
const parseBRL = (s: string) => {
    const cleaned = (s || '').trim();
    if (!cleaned) return 0;
    return parseFloat(cleaned.replace(/\./g, '').replace(',', '.')) || 0;
};
const round2 = (n: number) => Math.round(n * 100) / 100;

// Valores iniciais do formulário — usados no useState E no reset pós-submit, para os
// dois nunca divergirem (era por isso que a tela voltava com dados da etapa 1).
/**
 * Chave de idempotência do pagamento (opcional no payload). `crypto.randomUUID` só
 * existe em contexto seguro, e o próprio global `crypto` pode não existir — sem esta
 * guarda o envio estourava "crypto is not defined" antes de chamar a API.
 */
const today = () => new Date().toISOString().slice(0, 10);
const INITIAL_TIME = '00:00';
const INITIAL_INV_SORT = 'title' as const;   // natureza + nº do título

/**
 * Valor esperado para as dicas de desconto/acréscimo: o do RASCUNHO, que já é o saldo do título
 * (`amount - amount_paid`, resolvido no `goToInvoices`).
 *
 * ⚠️ Descontar `amountPaid` daqui subtraía o pago DUAS vezes — num título de 1000 com 300 pagos, o
 * campo carregava 700 e a dica cobrava 400, acusando acréscimo de 300 em pagamento correto.
 */
const invoiceExpected = (inv: Invoice) => round2(inv.amount);

/**
 * RASCUNHO de fatura — uma por TÍTULO (charge) com dívida viva, não uma por fatura existente.
 *
 * A troca de granularidade é o que faz "liquidar" e "parcial" voltarem a significar o que dizem:
 * o rascunho cobre o saldo INTEIRO do título, então não há fração a considerar nem faturas irmãs
 * a conciliar. `invoiceId` é a fatura ORIGINAL do charge — a de quem o rascunho deriva.
 */
interface Invoice {
    id: number;
    /** fatura ORIGINAL do charge: é dela que a fatura nova deriva */
    invoiceId: string;
    chargeId: string | null;
    creditOperationId: string;
    /** saldo do TÍTULO (amount - paid_amount): é o que o rascunho cobra */
    amount: number;
    /** valor NOMINAL do título, exibido abaixo do valor a cobrar */
    nominalAmount?: number | null;
    amountPaid: number | null;
    dueDate: string;
    status: string;
    statusFormatted?: string;
    documentTitle?: string | null;
    /** nº do título (fraciona a OPERAÇÃO: "Parcela 3", "Recebível 12") */
    installmentNumber?: number | null;
    contractLabel?: string | null;
    payer?: { name: string; documentNumber: string | null };
    /** cenário do charge — decide quais modos de liquidação a tela pode oferecer */
    chargeScenario?: string;
    allowsPartialPayment?: boolean;
    /** modo que a própria fatura declarava (tri-state; null = baldinho) */
    totalPayment?: boolean | null;
    /** o título já tem fatura com pagamento sinalizado (vw_charges_list.has_signalled_payment) */
    hasSignalledPayment?: boolean;
    _op?: string; // nº do contrato (para exibição)
}

interface ItemEdit {
    value: string;          // BRL string editável
    /** TOTAL | PARCIAL | OPERATION_WATERFALL — convertido em total_payment tri-state no submit */
    mode: LiquidationMode;
    cancel_invoice: boolean;
    uncollect: boolean;
}

/**
 * Modos válidos para esta fatura. Sem `chargeScenario` (API antiga) cai em GUARANTEE, que só
 * aceita total — o mesmo que a tela fazia antes deste campo existir.
 */
const modesOf = (inv: Invoice): LiquidationMode[] =>
    PAYMENT_MODES_BY_SCENARIO[inv.chargeScenario || 'GUARANTEE'] || ['TOTAL'];

interface Props {
    /** default REGISTER para as rotas e testes existentes seguirem valendo sem mudança */
    mode?: PageMode;
}

const RegisterPaymentPage: React.FC<Props> = ({ mode = 'REGISTER' }) => {
    const identifying = mode === 'IDENTIFY';
    const STEPS = STEPS_BY_MODE[mode];
    const [step, setStep] = useState(0);
    /** modo IDENTIFY: o pagamento já registrado que este trabalho vai identificar */
    const [selectedPayment, setSelectedPayment] = useState<PaymentRow | null>(null);
    const [infoPayment, setInfoPayment] = useState<PaymentRow | null>(null);
    /**
     * Chave do pagamento, estável durante o preenchimento. Gerada aqui e NÃO no submit: dois
     * cliques em "Concluir" (ou um retry depois de timeout) mandavam ids diferentes e nasciam dois
     * pagamentos, porque a idempotência do consumidor é por chave. O reset da tela sorteia a
     * próxima.
     */
    const [paymentId, setPaymentId] = useState(() => newPaymentId());

    // ── Step 1 ──
    const [amount, setAmount] = useState('');
    const [date, setDate] = useState(today);
    const [time, setTime] = useState(INITIAL_TIME);
    const [showTime, setShowTime] = useState(false);
    const [typeFields, setTypeFields] = useState<PaymentTypeValue>(emptyPaymentTypeValue());
    const [notes, setNotes] = useState('');
    const [receiptFile, setReceiptFile] = useState<File | null>(null);
    const [receiptName, setReceiptName] = useState('');
    const [payer, setPayer] = useState<PayerValue>(() => emptyPayer());
    // "Responsável" não é mais campo de tela: é quem está logado realizando a ação
    const auth = useContext(AuthContext);
    const userResponsible = auth?.user?.username || auth?.user?.email || '';

    // ── Step 2 ──
    const [searching, setSearching] = useState(false);
    const [results, setResults] = useState<SettlementOperation[]>([]);
    const [opFilters, setOpFilters] = useState<AppliedFilter[]>([]);
    const [opPage, setOpPage] = useState(0);
    const [opTotal, setOpTotal] = useState(0);
    const [opSearched, setOpSearched] = useState(false);
    const [selectedOps, setSelectedOps] = useState<SettlementOperation[]>([]);

    // ── Step 3 ──
    const [invoices, setInvoices] = useState<Invoice[]>([]);
    const [loadingInvoices, setLoadingInvoices] = useState(false);
    const [edits, setEdits] = useState<Record<string, ItemEdit>>({}); // key = invoiceId (selecionados)
    const [invSearch, setInvSearch] = useState('');
    const [invSort, setInvSort] = useState<'title' | 'dueDate' | 'amount' | 'payer'>(INITIAL_INV_SORT);
    const [submitting, setSubmitting] = useState(false);
    // resumo da operação (contrato/aditamento/valor/pago) p/ cabeçalho dos grupos — via settlement /operations
    const [openSel, setOpenSel] = useState(true);   // accordion: faturas selecionadas
    const [openList, setOpenList] = useState(true);  // accordion: listagem/seleção

    /**
     * O valor que precisa ser todo alocado.
     *
     * No modo IDENTIFY ele vem do PAGAMENTO escolhido, não de um campo: o dinheiro já entrou e o
     * valor dele é fato gravado. Deixar o usuário digitar aqui permitiria fechar a conta contra um
     * número que não é o que o banco recebeu.
     */
    const paymentAmount = identifying
        ? round2(Number(selectedPayment?.amount || 0))
        : round2(parseBRL(amount));

    // IDENTIFY: basta ter escolhido um pagamento — os dados dele já foram validados no passo 1.
    // REGISTER: conta de recebimento só é exigida em pagamento financeiro.
    const step1Valid = identifying
        ? !!selectedPayment && paymentAmount > 0
        : paymentAmount > 0 && !!date
            && (typeFields.payment_type !== PaymentType.FINANCIAL || !!typeFields.receiving_bank_account)
            && onlyDigits(payer.document).length >= 11;

    // ─── Step 2: busca de operações (settlement) ─────────────────────────────
    // GET /operations (settlement_v2.vw_operations_list). Vem daqui e não do contracts_management
    // porque nesta tela a operação interessa pelo que ela DEVE — `amount`, a soma dos títulos — e o
    // contracts_management só conhece o `nominal_value` do credit, que não responde por isso.
    //
    // `status = 'Finalizado com sucesso'` e `billed IS TRUE` já são chumbados no servidor (sem
    // faturamento não existe fatura para receber), então não vão mais como filtro fixo.
    const OP_PAGE_SIZE = 20;
    const fetchOperations = useCallback(async (filters: AppliedFilter[], page: number) => {
        setSearching(true);
        try {
            const params = serializeFilters(filters);
            // o settlement pagina em 1-based (`page`/`size`); o FilterBuilder entrega 0-based
            params.set('page', String(page + 1));
            params.set('size', String(OP_PAGE_SIZE));
            params.set('sort', '-sequencial_number');
            const data = await listOperations(Object.fromEntries(params));
            setResults(data.list);
            setOpTotal(data.totalElements);
            setOpSearched(true);
        } catch (e: any) {
            toast.error(`Erro ao buscar operações: ${e.message}`);
        } finally {
            setSearching(false);
        }
    }, []);

    const runSearch = useCallback((filters: AppliedFilter[]) => {
        setOpFilters(filters);
        setOpPage(0);
        fetchOperations(filters, 0);
    }, [fetchOperations]);

    const changeOpPage = (page: number) => {
        setOpPage(page);
        fetchOperations(opFilters, page);
    };

    // O participante preferencial (ASSIGNOR/BORROWER) a view já resolve — issuer_name/document.
    // O RÓTULO não usa o contract_label pronto de propósito: ele é sempre "n-a", e esta tela
    // omite o aditamento zero. Trocar mudaria o que o operador lê na busca, sem ninguém pedir.
    const opLabel = (op: SettlementOperation) => {
        const contract = op.sequencial_number ?? '—';
        return op.amendment_number && op.amendment_number > 0 ? `${contract}-${op.amendment_number}` : `${contract}`;
    };

    const toggleOp = (op: SettlementOperation) => {
        setSelectedOps(prev => prev.find(o => o.credit_operation_id === op.credit_operation_id)
            ? prev.filter(o => o.credit_operation_id !== op.credit_operation_id)
            : [...prev, op]);
    };
    const isOpSelected = (op: SettlementOperation) => !!selectedOps.find(o => o.credit_operation_id === op.credit_operation_id);

    // ─── Step 3: um RASCUNHO por título com dívida viva ──────────────────────
    // Antes listava as faturas OPEN de cada operação (1 linha = 1 fatura) e filtrava no cliente.
    // Agora parte dos CHARGES com título OPEN/PARTIALLY_PAID: 1 linha = 1 título, e cada linha é o
    // rascunho da fatura que vai ser criada, pelo saldo inteiro daquele título. O filtro de status
    // é do TÍTULO e vai para o servidor — o `size` deixou de cortar antes de filtrar.
    const goToInvoices = async () => {
        setLoadingInvoices(true);
        try {
            const all: Invoice[] = [];
            for (const op of selectedOps) {
                const data = await listOpenChargesByOperation(op.credit_operation_id);
                (data.items || data.list || [])
                    // sem fatura original não há de quem derivar (charge que nunca foi faturado)
                    .filter((c: any) => c.originalInvoiceId)
                    .forEach((c: any) => all.push({
                        id: 0,
                        invoiceId: c.originalInvoiceId,
                        chargeId: c.chargeId,
                        creditOperationId: c.creditOperationId,
                        amount: Number(c.remainingAmount ?? c.amount ?? 0),
                        nominalAmount: c.amount,
                        amountPaid: c.paidAmount,
                        dueDate: c.dueDate,
                        status: c.titleDebtStatus || 'OPEN',
                        documentTitle: c.documentTitle,
                        installmentNumber: c.installmentNumber,
                        contractLabel: c.contractLabel,
                        payer: c.payer,
                        chargeScenario: c.chargeScenario,
                        allowsPartialPayment: true,
                        totalPayment: undefined,
                        // o título já tem marcação pendente de outro pagamento (changelog_024).
                        // Sem carregar aqui, a linha entraria selecionável e o usuário montaria o
                        // lote todo para receber a recusa do servidor no fim.
                        hasSignalledPayment: !!c.hasSignalledPayment,
                        _op: op.operation_number ?? undefined,
                    }));
            }
            setInvoices(all);
            setStep(2);
        } catch (e: any) {
            toast.error(`Erro ao carregar faturas: ${e.message}`);
        } finally {
            setLoadingInvoices(false);
        }
    };

    // o default é o modo que a PRÓPRIA fatura declarava, limitado ao que o cenário aceita:
    // uma parcela paga por garantia nasce baldinho, e forçar TOTAL aqui liquidaria o título
    const defaultEdit = (inv: Invoice): ItemEdit => ({
        value: fmt(inv.amount),
        mode: clampMode(modeOf(inv.totalPayment), modesOf(inv)),
        cancel_invoice: true,
        uncollect: true,
    });

    const toggleInvoice = (inv: Invoice) => {
        setEdits(prev => {
            const next = { ...prev };
            if (next[inv.invoiceId]) delete next[inv.invoiceId];
            else next[inv.invoiceId] = defaultEdit(inv);
            return next;
        });
    };

    const patchEdit = (inv: Invoice, patch: Partial<ItemEdit>) => {
        setEdits(prev => {
            const cur = prev[inv.invoiceId];
            if (!cur) return prev;
            let next = { ...cur, ...patch };
            // value alterado → ajusta o modo automaticamente (usuário ainda pode remarcar).
            // Duas exceções: OPERATION_WATERFALL é uma DECLARAÇÃO da fatura (o dinheiro entra na operação,
            // não no título) e não se troca por comparação de valor; e cenário que não aceita
            // PARCIAL (garantia) não pode ser rebaixado.
            const allowedModes = modesOf(inv);
            if (patch.value !== undefined && cur.mode !== 'OPERATION_WATERFALL') {
                const v = round2(parseBRL(patch.value));
                const target: LiquidationMode = v < inv.amount ? 'PARTIAL' : 'TOTAL';
                if (allowedModes.includes(target)) next.mode = target;
            }
            // cancel_invoice=true força uncollect=true (e trava); false libera uncollect
            if (patch.cancel_invoice === true) next.uncollect = true;
            return { ...prev, [inv.invoiceId]: next };
        });
    };

    const isSelected = (inv: Invoice) => !!edits[inv.invoiceId];

    const filteredInvoices = useMemo(() => {
        const q = invSearch.trim().toLowerCase();
        let list = invoices;
        if (q) {
            list = list.filter(inv =>
                (inv.payer?.name || '').toLowerCase().includes(q) ||
                (inv.chargeId || '').toLowerCase().includes(q) ||
                (inv.documentTitle || '').toLowerCase().includes(q) ||
                (inv._op || '').toLowerCase().includes(q));
        }
        const sorted = [...list].sort((a, b) => {
            if (invSort === 'amount') return b.amount - a.amount;
            if (invSort === 'payer') return (a.payer?.name || '').localeCompare(b.payer?.name || '');
            if (invSort === 'title') {
                // agrupa por natureza e ordena pelo nº do título: as 16 garantias saem em sequência,
                // as parcelas em outra — ler "Recebível 1..16" na ordem é o que o usuário confere
                const byScenario = (a.chargeScenario || '').localeCompare(b.chargeScenario || '');
                if (byScenario !== 0) return byScenario;
                return (a.installmentNumber ?? 0) - (b.installmentNumber ?? 0);
            }
            return (a.dueDate || '').localeCompare(b.dueDate || '');
        });
        return sorted;
    }, [invoices, invSearch, invSort]);

    // agrupa os invoices filtrados por operação, preservando a ordem das operações selecionadas
    const groups = useMemo(() => {
        const byOp: Record<string, Invoice[]> = {};
        for (const inv of filteredInvoices) (byOp[inv.creditOperationId] ||= []).push(inv);
        return selectedOps
            .map(op => ({ op, list: byOp[op.credit_operation_id] || [] }))
            .filter(g => g.list.length > 0);
    }, [filteredInvoices, selectedOps]);

    // invoices selecionados (na ordem em que foram carregados)
    const selectedInvoices = useMemo(() => invoices.filter(inv => edits[inv.invoiceId]), [invoices, edits]);

    const allocated = useMemo(() =>
        round2(Object.values(edits).reduce((s, e) => s + parseBRL(e.value), 0)), [edits]);
    const pct = paymentAmount > 0 ? Math.min(100, (allocated / paymentAmount) * 100) : 0;
    const over = allocated > paymentAmount;
    const canSubmit = paymentAmount > 0 && round2(allocated) === paymentAmount && Object.keys(edits).length > 0;

    // ─── Submit ──────────────────────────────────────────────────────────────
    const buildPayload = () => {
        const payment_date = toIsoInstant(date, time, showTime)!; // wall-clock local → UTC (sem D±1)
        const doc = onlyDigits(payer.document);
        const items: PaymentInvoiceItem[] = invoices
            .filter(inv => edits[inv.invoiceId])
            .map(inv => {
                const e = edits[inv.invoiceId];
                return {
                    credit_operation_id: inv.creditOperationId,
                    charge_id: inv.chargeId,
                    invoice_id: inv.invoiceId,
                    value: round2(parseBRL(e.value)),
                    total_payment: MODE_TO_TOTAL_PAYMENT[e.mode],
                    // Pagamento parcial: a fatura nova cobre uma PARTE do título, não ele inteiro.
                    // Herdar o 1/1 da original faria a cobrança parecer completa, e o charge não
                    // apareceria como "Falta faturar" apesar do saldo devedor. 1/2 é a afirmação
                    // mínima de "isto é um pedaço" — quantas faturas virão ainda não se sabe, e o
                    // que a tela de Títulos precisa saber é que falta alguma.
                    ...(e.mode === 'PARTIAL'
                        ? { installment_number: 1, total_installments: 2 } : {}),
                    cancel_invoice: e.cancel_invoice,
                    uncollect: e.uncollect,
                    // Aqui a linha é um RASCUNHO por TÍTULO, não uma fatura: o usuário não
                    // escolheu qual cancelar, escolheu o que fazer com a cobrança daquele
                    // título. O default do backend é a fatura sozinha — quem age sobre uma
                    // fatura específica (modal de Liquidar/Baixa Parcial) não manda nada.
                    cancel_scope: 'CHARGE' as const,
                };
            });
        // o bloco do DINHEIRO é compartilhado com o modal Registrar Pagamento (passo 1):
        // duplicá-lo faria uma regra corrigida aqui continuar errada lá
        return {
            ...buildPaymentBlock({
                amount: paymentAmount,
                paymentDate: payment_date,
                typeFields,
                payer,
                paymentId,
                notes,
                receiptName,
                userResponsible,
            }),
            invoices: items,
            // o builder devolve o bloco solto (Record) para servir aos dois chamadores; a forma
            // completa só existe quando as faturas entram, e é aqui que ela se fecha
        } as ProcessPaymentPayload;
    };

    /** Volta a tela inteira (as 3 etapas) ao estado inicial. */
    const resetForm = () => {
        setStep(0);
        setPaymentId(newPaymentId());   // concluído: o próximo registro é outro comando
        // o pagamento identificado sai da fila de pendentes; manter a escolha faria a tela
        // oferecer um pagamento que o servidor já vai recusar
        setSelectedPayment(null);
        // etapa 1
        setAmount(''); setDate(today()); setTime(INITIAL_TIME); setShowTime(false);
        setTypeFields(emptyPaymentTypeValue()); setNotes(''); setReceiptName(''); setReceiptFile(null);
        setPayer(emptyPayer());
        // etapa 2
        setResults([]); setOpFilters([]); setOpPage(0); setOpTotal(0);
        setOpSearched(false); setSelectedOps([]);
        // etapa 3
        setInvoices([]); setEdits({}); setInvSearch(''); setInvSort(INITIAL_INV_SORT);
        setOpenSel(true); setOpenList(true);
        // PayerFields, FilterBuilder e o select de conta têm estado interno próprio, mas
        // desmontam ao trocar de etapa — voltar para a etapa 1 já os remonta limpos.
    };

    /** Itens da MARCAÇÃO (modo IDENTIFY) — o mesmo recorte por título do payload antigo. */
    const buildMarkItems = (): MarkInvoiceItem[] => invoices
        .filter(inv => edits[inv.invoiceId])
        .map(inv => {
            const e = edits[inv.invoiceId];
            return {
                invoice_id: inv.invoiceId,
                value: round2(parseBRL(e.value)),
                payment_action_promise: draftPromise(e.cancel_invoice, e.uncollect),
                total_payment: MODE_TO_TOTAL_PAYMENT[e.mode],
                // mesma razão do fluxo antigo: a fatura nova cobre uma PARTE do título, e herdar
                // o 1/1 faria a cobrança parecer completa apesar do saldo devedor
                ...(e.mode === 'PARTIAL'
                    ? { installment_number: 1, total_installments: 2 } : {}),
                notes: notes || undefined,
            };
        });

    const submit = async () => {
        setSubmitting(true);
        try {
            if (identifying) {
                const res = await markInvoices(selectedPayment!.paymentId, buildMarkItems());
                const n = res?.data?.marked?.length ?? res?.marked?.length ?? 0;
                toast.success(
                    `${n} fatura(s) sinalizada(s). Aguardando aprovação do financeiro.`);
                resetForm();
                return;
            }
            // comprovante primeiro: o nome canônico vem do backend e viaja no payload
            if (receiptFile) {
                const uploaded = await uploadPaymentReceipt(
                    paymentId, receiptFile, toIsoInstant(date, time, showTime)!, payer.name,
                );
                setReceiptName(uploaded.fileName);
            }
            const res = await processPayment(buildPayload());
            const n = res?.data?.settled?.length ?? 0;
            toast.success(`Pagamento registrado — ${n} fatura(s) processada(s)`);
            resetForm();
        } catch (e: any) {
            toast.error(`Erro ao registrar pagamento: ${e.message}`);
        } finally {
            setSubmitting(false);
        }
    };

    const input = 'w-full border rounded px-3 py-2 text-sm dark:bg-slate-700 dark:border-slate-600 dark:text-white disabled:bg-gray-100 disabled:text-gray-500 dark:disabled:bg-slate-800 disabled:cursor-not-allowed';
    const label = 'text-xs text-gray-500 dark:text-gray-400 block mb-1';

    const fmtDate = (d?: string) => (d ? d.split('-').reverse().join('/') : '—');

    // cabeçalho enriquecido do invoice (documento · pagador … valor / Fatura · op · Recebível · vence)
    const invHeader = (inv: Invoice) => (
        <div className="flex-1 min-w-0">
            <div className="flex justify-between gap-2">
                <span className="text-sm font-medium truncate">
                    {inv.payer?.documentNumber ? `${formatTaxId(inv.payer.documentNumber)} · ` : ''}{inv.payer?.name || '—'}
                </span>
                <span className="text-sm font-medium whitespace-nowrap">R$ {fmt(inv.amount)}</span>
            </div>
            <div className="flex justify-between gap-2 text-xs text-gray-500">
                <span className="truncate">
                    {/* Da operação para a fatura, do mais amplo ao mais específico. Contrato-
                        aditamento como nas colunas de Contrato das telas de Operações e Títulos —
                        uuid de operação não diz nada a quem lê */}
                    Operação {inv.contractLabel || inv._op || inv.creditOperationId.slice(0, 8)}
                    {inv.chargeId ? ` · #${inv.chargeId.slice(0, 8)}` : ''}
                    {/* Ordem padrão das telas de cobrança: natureza, Título/NF, Nº.
                        O número do título anda com rótulo PRÓPRIO ("Nº 2") e não colado na
                        natureza ("Garantia 2") — ali a palavra nomeava o número, e é a natureza. */}
                    {inv.chargeScenario && (
                        <> · {CHARGE_SCENARIO_LABELS[inv.chargeScenario] || inv.chargeScenario}</>
                    )}
                    {/* Título/NF e NÃO o id da fatura: estas linhas são RASCUNHOS — a fatura ainda
                        não existe, e o `invoiceId` aqui é o da fatura de ORIGEM, que não diz nada
                        sobre o que está sendo cobrado. Sem documento, mostra o rótulo vazio em vez
                        de sumir: a ausência do documento é informação. */}
                    {' · '}Título/NF {inv.documentTitle || '-'}
                    {inv.installmentNumber != null && <> · Nº {inv.installmentNumber}</>}
                </span>
                {/* valor NOMINAL na mesma linha do vencimento: sem ele o usuário não vê que o
                    rascunho é o SALDO (nominal menos o que já foi pago) */}
                <span className="whitespace-nowrap">
                    {inv.nominalAmount != null && Number(inv.nominalAmount) !== inv.amount && (
                        <>Valor Nominal R$ {fmt(Number(inv.nominalAmount))} · </>
                    )}
                    vence {fmtDate(inv.dueDate)}
                </span>
            </div>
        </div>
    );

    /**
     * Efeito no título. O widget muda com o cenário porque as escolhas mudam:
     *   RECEIVABLE  → checkbox liquida/não liquida (como sempre foi)
     *   INSTALLMENT → select com os três, incl. baldinho (pago pela garantia)
     *   GUARANTEE   → mesmo checkbox do recebível, decidindo o efeito no título de GARANTIA, com
     *                 a nota de que o valor também é distribuído pelas parcelas da operação
     *   desconhecido → travado em total: cenário que esta versão da tela não conhece (API mais
     *                 nova) degrada para o modo mais conservador em vez de adivinhar
     */
    const liquidationControl = (inv: Invoice, e: ItemEdit) => {
        const allowedModes = modesOf(inv);
        const cor = e.mode === 'TOTAL' ? 'bg-green-500' : 'bg-amber-500';

        if (allowedModes.length <= 1) {
            return (
                <div className="mt-5 self-start">
                    <span className="inline-block px-2 py-1 rounded text-sm bg-green-500 text-white">
                        {MODE_LABELS.TOTAL}
                    </span>
                    <p className="text-xs text-gray-500 mt-1">
                        Cenário não reconhecido por esta tela: só liquidação total.
                    </p>
                </div>
            );
        }
        if (allowedModes.includes('OPERATION_WATERFALL')) {
            return (
                <div className="mt-5 self-start w-full">
                    {/* Sem rótulo VISÍVEL: as opções já estão no imperativo ("Liquidar o Título")
                        e dizem sozinhas o que fazem. O aria-label fica porque tirar o <label>
                        deixaria o select sem nome nenhum para leitor de tela — e a explicação do
                        baldinho vai no title, não numa legenda fixa que empurra a linha e só
                        interessa a quem escolheu essa opção. */}
                    <select id={`modo-${inv.invoiceId}`} aria-label="Efeito no título"
                        className={`${input} ${cor} text-white border-0`}
                        value={e.mode}
                        title={e.mode === 'OPERATION_WATERFALL' ? WATERFALL_HINT : undefined}
                        onChange={ev => patchEdit(inv, { mode: ev.target.value as LiquidationMode })}>
                        {allowedModes.map(m => (
                            <option key={m} value={m}
                                title={m === 'OPERATION_WATERFALL' ? WATERFALL_HINT : undefined}>
                                {MODE_ACTIONS[m]}
                            </option>
                        ))}
                    </select>
                </div>
            );
        }
        // estado destacado nas cores dos botões da tela de títulos:
        // verde = liquida (Liquidar), âmbar = não liquida (Baixa Parcial)
        return (
            <div className="mt-5 self-start">
                <label className={`flex items-center gap-2 text-sm px-2 py-2 rounded cursor-pointer transition-colors self-start ${e.mode === 'TOTAL'
                    ? 'bg-green-500 hover:bg-green-600 text-white'
                    : 'bg-amber-500 hover:bg-amber-600 text-white'
                    }`}>
                    <input type="checkbox" className="accent-white" checked={e.mode === 'TOTAL'}
                        onChange={ev => patchEdit(inv, { mode: ev.target.checked ? 'TOTAL' : 'PARTIAL' })} />
                    Quitação total do título
                </label>
                {inv.chargeScenario === 'GUARANTEE' && (
                    <p className="text-xs text-gray-500 mt-1">
                        Efeito no título de <strong>garantia</strong>. O valor é também distribuído pelas
                        parcelas da operação, nos dois casos.
                    </p>
                )}
            </div>
        );
    };

    const editControls = (inv: Invoice, e: ItemEdit) => (
        <div className="grid grid-cols-2 gap-x-6 gap-y-2 mt-2" onClick={ev => ev.stopPropagation()}>
            <div>
                <label className={label}>Valor pago (R$)</label>
                <input className={input} value={e.value} onChange={ev => patchEdit(inv, { value: ev.target.value })} />
                {/* desconto/acréscimo só quando quita o título (em pagamento parcial o valor
                    menor é expected, não é desconto) */}
                <HintBadge hint={e.mode === 'TOTAL' ? amountHint(parseBRL(e.value), invoiceExpected(inv)) : null} />
            </div>
            {liquidationControl(inv, e)}
            {/* o efeito é sobre TODAS as faturas abertas daquele charge, não só a original: pagar
                a dívida do título torna as irmãs de uma quebra em frações cobrança de valor já
                pago. O escopo por charge é resolvido no backend (ProcessPayment._apply_flags). */}
            <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={e.cancel_invoice} onChange={ev => patchEdit(inv, { cancel_invoice: ev.target.checked })} />
                Cancelar faturas abertas
            </label>
            <label className={`flex items-center gap-2 text-sm ${e.cancel_invoice ? 'opacity-50' : ''}`}>
                <input type="checkbox" checked={e.uncollect} disabled={e.cancel_invoice} onChange={ev => patchEdit(inv, { uncollect: ev.target.checked })} />
                Parar de cobrar faturas abertas
            </label>
        </div>
    );

    return (
        <PageForm
            pageTitle={PAGE_TITLE[mode]}
            isLoading={submitting}
            breadcrumb={[
                { label: 'Home', url: '/home' },
                { label: 'Financeiro', url: '#' },
                { label: PAGE_TITLE[mode], url: PAGE_ROUTE[mode] },
            ]}
            apiVersion=""
            /* O stepper é o controle desta tela: fica no slot do template, grudado no cabeçalho,
               em vez de rolar junto com o formulário. */
            controlSlot={<Stepper step={step} steps={STEPS} />}
        >
            <div className="py-4 max-w-6xl mx-auto">

            {/* ═══ STEP 1 — IDENTIFY: escolher um pagamento que já entrou ═══ */}
            {step === 0 && identifying && (
                <div className="bg-white dark:bg-slate-800 rounded-lg shadow-sm p-6">
                    <h2 className="font-semibold text-gray-800 dark:text-white mb-1">
                        Selecionar Pagamento
                    </h2>
                    <p className="text-xs text-gray-500 mb-4">
                        O dinheiro já entrou — registrado pelo financeiro na tela de Pagamentos.
                        Aqui você diz <strong>quais faturas ele paga</strong>. As faturas ficam
                        sinalizadas e só são liquidadas quando o financeiro aprovar a identificação.
                    </p>

                    <PendingPaymentPicker
                        selectedId={selectedPayment?.paymentId}
                        onSelect={setSelectedPayment}
                        onInfo={setInfoPayment}
                    />

                    {selectedPayment && (
                        <div className="mt-4 px-3 py-2 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded text-sm">
                            A conta a fechar nas próximas etapas é{' '}
                            <strong>R$ {fmt(paymentAmount)}</strong>
                            {selectedPayment.payer?.name && <> · {selectedPayment.payer.name}</>}
                        </div>
                    )}

                    <div className="mt-6 flex justify-end">
                        <button disabled={!step1Valid} onClick={() => setStep(1)}
                            className="px-6 py-2 bg-blue-600 text-white rounded-lg disabled:opacity-50 hover:bg-blue-700">Avançar →</button>
                    </div>
                </div>
            )}

            {/* ═══ STEP 1 — REGISTER (deprecated): informar o dinheiro na mão ═══ */}
            {step === 0 && !identifying && (
                <div className="bg-white dark:bg-slate-800 rounded-lg shadow-sm p-6">
                    <h2 className="font-semibold text-gray-800 dark:text-white mb-4">Dados do Recebimento</h2>
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className={label}>Data do Pagamento *</label>
                            <PaymentDateInput date={date} time={time} showTime={showTime} className={input}
                                onChange={next => { setDate(next.date); setTime(next.time); setShowTime(next.showTime); }} />
                        </div>
                        <div>
                            <label className={label}>Valor Recebido (R$) *</label>
                            <input type="text" value={amount} onChange={e => setAmount(e.target.value)}
                                placeholder="2.500.000,00" className={`${input} text-lg font-semibold`} />
                        </div>
                    </div>

                    {/* Tipo de pagamento + campos dependentes (mesmo bloco da baixa de título) */}
                    <div className="mt-4">
                        <PaymentTypeFields
                            value={typeFields}
                            onChange={next => {
                                // dação: o valor recebido acompanha o total dos bens
                                const total = assetDationTotal(next.asset_dation_list);
                                if (next.payment_type === PaymentType.ASSET_DATION
                                    && total !== assetDationTotal(typeFields.asset_dation_list)) {
                                    setAmount(total.toFixed(2).replace('.', ','));
                                }
                                setTypeFields(next);
                            }}
                            methods={[PaymentMethod.PIX, PaymentMethod.TRANSFER, PaymentMethod.BOLETO]}
                            labelClassName={label}
                            inputClassName={input}
                            idPrefix="register-payment"
                        />
                    </div>

                    {/* Pagador (bloco compartilhado com a baixa de título) */}
                    <div className="mt-6 border-t dark:border-slate-700 pt-4">
                        <PayerFields value={payer} onChange={setPayer}
                            labelClassName={label} inputClassName={input} />
                    </div>

                    <div className="mt-6 border-t dark:border-slate-700 pt-4">
                        <label className={label}>Comprovante de pagamento (opcional)</label>
                        <label className="flex items-center gap-2 border border-dashed dark:border-slate-600 rounded px-3 py-3 text-sm text-gray-500 cursor-pointer hover:bg-gray-50 dark:hover:bg-slate-700">
                            <FaCloudUploadAlt /> {receiptFile?.name || 'Selecionar arquivo PDF'}
                            <input type="file" accept="application/pdf" className="hidden"
                                onChange={e => setReceiptFile(e.target.files?.[0] || null)} />
                        </label>
                    </div>

                    <div className="mt-4">
                        <label className={label}>Observações</label>
                        <input type="text" value={notes} onChange={e => setNotes(e.target.value)}
                            placeholder="Referência bancária, NF, etc." className={input} />
                    </div>

                    <div className="mt-6 flex justify-end">
                        <button disabled={!step1Valid} onClick={() => setStep(1)}
                            className="px-6 py-2 bg-blue-600 text-white rounded-lg disabled:opacity-50 hover:bg-blue-700">Avançar →</button>
                    </div>
                </div>
            )}

            {/* ═══ STEP 2 ═══ */}
            {step === 1 && (
                <>
                    <div className="flex items-center justify-between mb-2">
                        <h2 className="font-semibold text-gray-800 dark:text-white">Buscar Operações de Crédito</h2>
                        <div className="text-sm font-semibold text-green-700">Valor: R$ {fmt(paymentAmount)}</div>
                    </div>

                    <FilterBuilder
                        schema={operationsFilterSchema}
                        onSearch={runSearch}
                        loading={searching}
                        title="Filtros de operações"
                    />

                    <div className="bg-white dark:bg-slate-800 rounded-lg shadow-sm">
                        {selectedOps.length > 0 && (
                            <div className="px-4 py-3 bg-blue-50 dark:bg-slate-700/40 border-b dark:border-slate-700">
                                <span className="text-xs font-semibold text-blue-700 uppercase">Selecionados:</span>
                                <div className="flex flex-wrap gap-2 mt-1">
                                    {selectedOps.map(o => (
                                        <span key={o.credit_operation_id} className="inline-flex items-center gap-1 px-2 py-1 bg-white dark:bg-slate-800 border border-blue-200 dark:border-slate-600 rounded text-sm">
                                            {opLabel(o)} <span className="text-gray-400 text-xs">{o.operation_number}</span>
                                            <button onClick={() => toggleOp(o)} className="text-gray-400 hover:text-red-500 ml-1"><FaTimes size={11} /></button>
                                        </span>
                                    ))}
                                </div>
                            </div>
                        )}

                        {results.length > 0 ? (
                            <>
                                <div className="divide-y dark:divide-slate-700 max-h-[50vh] overflow-y-auto">
                                    {results.map(op => {
                                        return (
                                            <div key={op.credit_operation_id} className="flex items-center px-4 py-3 hover:bg-gray-50 dark:hover:bg-slate-700/40 cursor-pointer"
                                                onClick={() => toggleOp(op)}>
                                                <span className="mr-3">{isOpSelected(op) ? <FaCheckSquare className="text-blue-600 text-lg" /> : <FaRegSquare className="text-lg text-gray-400" />}</span>
                                                <div className="flex-1 min-w-0">
                                                    <div className="flex items-baseline gap-2">
                                                        <span className="font-medium">{opLabel(op)}</span>
                                                        <span className="text-xs text-gray-500 truncate">{op.operation_number}</span>
                                                        <span className="text-xs px-2 py-0.5 bg-gray-100 dark:bg-slate-700 rounded whitespace-nowrap">{op.operation_type}</span>
                                                    </div>
                                                    <div className="text-xs text-gray-500 truncate">
                                                        {op.issuer_document ? `${formatTaxId(op.issuer_document)} · ` : ''}{op.issuer_name || '—'}
                                                        {op.credit_product_type_name ? ` · ${op.credit_product_type_name}` : ''}
                                                    </div>
                                                </div>
                                                <div className="text-right whitespace-nowrap ml-2">
                                                    {/* `amount` = soma dos títulos: é o que a
                                                        operação de fato deve, e é com ele que o
                                                        recebimento é conciliado. NÃO o
                                                        nominal_value, que vem do credit e não
                                                        responde por isso. Sem título ainda, não
                                                        há o que conciliar — mostra "—" em vez de
                                                        cair num número de outra fonte. */}
                                                    <div className="text-sm">
                                                        {op.amount != null ? `R$ ${fmt(Number(op.amount))}` : '—'}
                                                    </div>
                                                    {op.amount != null && (op.paid_amount ?? 0) > 0 && (
                                                        <div className="text-xs text-green-600">recebido R$ {fmt(Number(op.paid_amount))}</div>
                                                    )}
                                                    {op.expiration_date && (
                                                        <div className="text-xs text-gray-500">vence {op.expiration_date.slice(0, 10).split('-').reverse().join('/')}</div>
                                                    )}
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                                {opTotal > OP_PAGE_SIZE && (
                                    <div className="flex items-center justify-between px-4 py-2 border-t dark:border-slate-700 text-xs text-gray-500">
                                        <span>{opTotal} operação(ões) · página {opPage + 1} de {Math.ceil(opTotal / OP_PAGE_SIZE)}</span>
                                        <div className="flex gap-2">
                                            <button disabled={opPage === 0 || searching} onClick={() => changeOpPage(opPage - 1)}
                                                className="px-2 py-1 border rounded disabled:opacity-40 dark:border-slate-600">← Anterior</button>
                                            <button disabled={(opPage + 1) * OP_PAGE_SIZE >= opTotal || searching} onClick={() => changeOpPage(opPage + 1)}
                                                className="px-2 py-1 border rounded disabled:opacity-40 dark:border-slate-600">Próxima →</button>
                                        </div>
                                    </div>
                                )}
                            </>
                        ) : (
                            <div className="p-8 text-center text-gray-400">
                                {searching ? 'Buscando...' : opSearched ? 'Nenhuma operação encontrada com esses filtros' : 'Use os filtros acima para buscar operações'}
                            </div>
                        )}

                        <div className="p-4 border-t dark:border-slate-700 flex items-center justify-between bg-gray-50 dark:bg-slate-800">
                            <span className="text-sm text-gray-600 dark:text-gray-300">{selectedOps.length} operação(ões) selecionada(s)</span>
                            <div className="flex gap-3">
                                <button onClick={() => setStep(0)} className="px-4 py-2 border rounded-lg dark:border-slate-600">← Voltar</button>
                                <button disabled={selectedOps.length === 0 || loadingInvoices} onClick={goToInvoices}
                                    className="px-6 py-2 bg-blue-600 text-white rounded-lg disabled:opacity-50 hover:bg-blue-700">
                                    {loadingInvoices ? 'Carregando...' : 'Avançar →'}
                                </button>
                            </div>
                        </div>
                    </div>
                </>
            )}

            {/* ═══ STEP 3 ═══ */}
            {step === 2 && (
                <div className="space-y-4">
                    {/* Barra de alocação (sempre visível) */}
                    <div className="bg-white dark:bg-slate-800 rounded-lg shadow-sm p-4">
                        <div className="flex items-center justify-between text-sm mb-2">
                            <span className="text-gray-600 dark:text-gray-300">Alocado: <b className={over ? 'text-red-600' : 'text-green-700'}>R$ {fmt(allocated)}</b> / R$ {fmt(paymentAmount)}</span>
                            <span className={`font-semibold ${over ? 'text-red-600' : round2(allocated) === paymentAmount ? 'text-green-600' : 'text-gray-500'}`}>
                                {over ? `Excede R$ ${fmt(allocated - paymentAmount)}` : round2(allocated) === paymentAmount ? '✓ 100%' : `Falta R$ ${fmt(paymentAmount - allocated)}`}
                            </span>
                        </div>
                        <div className="w-full h-3 bg-gray-200 dark:bg-slate-700 rounded overflow-hidden">
                            <div className={`h-full transition-all ${over ? 'bg-red-500' : pct >= 100 ? 'bg-green-500' : 'bg-blue-500'}`} style={{ width: `${pct}%` }} />
                        </div>
                    </div>

                    {/* ── Accordion A: Faturas selecionadas (editar + submit) ── */}
                    <div className="bg-white dark:bg-slate-800 rounded-lg shadow-sm overflow-hidden">
                        <button onClick={() => setOpenSel(v => !v)}
                            className="w-full flex items-center justify-between px-4 py-3 font-semibold text-gray-800 dark:text-white bg-gray-300 dark:bg-slate-700 hover:bg-gray-300/80">
                            <span>Faturas selecionadas: {selectedInvoices.length}</span>
                            {openSel ? <FaChevronUp className="text-gray-400" /> : <FaChevronDown className="text-gray-400" />}
                        </button>
                        {openSel && (
                            <div className="border-t dark:border-slate-700 max-h-[45vh] overflow-y-auto bg-gray-50 dark:bg-slate-900/40 p-3 space-y-2">
                                {selectedInvoices.length === 0 && (
                                    <div className="p-4 text-center text-gray-400 text-sm">Nenhuma fatura selecionada — selecione na área abaixo</div>
                                )}
                                {selectedInvoices.map(inv => {
                                    const e = edits[inv.invoiceId];
                                    return (
                                        <div key={inv.invoiceId} className="bg-[#f0f8ff] dark:bg-slate-700/40 rounded-md border border-blue-200 dark:border-slate-700 px-4 py-3">
                                            <div className="flex items-start gap-3">
                                                {invHeader(inv)}
                                                <button onClick={() => toggleInvoice(inv)} title="Remover da seleção"
                                                    className="text-gray-400 hover:text-red-500 mt-0.5"><FaTimes /></button>
                                            </div>
                                            {e && editControls(inv, e)}
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>

                    {/* ── Accordion B: Operações / Faturas (listagem + seleção) ── */}
                    <div className="bg-white dark:bg-slate-800 rounded-lg shadow-sm overflow-hidden">
                        <button onClick={() => setOpenList(v => !v)}
                            className="w-full flex items-center justify-between px-4 py-3 font-semibold text-gray-800 dark:text-white bg-gray-300 dark:bg-slate-700 hover:bg-gray-300/80">
                            <span>Operações: {selectedOps.length} · Faturas: {invoices.length}</span>
                            {openList ? <FaChevronUp className="text-gray-400" /> : <FaChevronDown className="text-gray-400" />}
                        </button>
                        {openList && (
                            <div className="border-t dark:border-slate-700">
                                <div className="p-3 border-b dark:border-slate-700 flex flex-col sm:flex-row gap-2 sm:items-center">
                                    <input value={invSearch} onChange={e => setInvSearch(e.target.value)}
                                        placeholder="Filtrar por pagador, recebível, contrato..." className={`${input} flex-1`} />
                                    <div className="flex items-center gap-2 whitespace-nowrap">
                                        <span className="text-xs text-gray-500">Ordenar:</span>
                                        <select value={invSort} onChange={e => setInvSort(e.target.value as any)} className={`${input} w-auto`}>
                                            <option value="title">Natureza e nº do título</option>
                                            <option value="dueDate">Vencimento</option>
                                            <option value="amount">Valor</option>
                                            <option value="payer">Pagador</option>
                                        </select>
                                    </div>
                                </div>
                                <div className="max-h-[45vh] overflow-y-auto bg-gray-50 dark:bg-slate-900/40 p-3 space-y-3">
                                    {groups.length === 0 && <div className="p-8 text-center text-gray-400">Nenhum título com dívida em aberto</div>}
                                    {groups.map(({ op, list }) => {
                                        return (
                                            <div key={op.credit_operation_id} className="rounded-md border border-gray-300 dark:border-slate-700 overflow-hidden">
                                                <div className="px-4 py-2 bg-gray-200 dark:bg-slate-700/50 text-sm font-medium flex flex-wrap gap-x-3 gap-y-1">
                                                    {/* valor e pago vêm da própria busca agora: a
                                                        lista é do settlement e já traz os dois.
                                                        Antes a tela fazia uma chamada por operação
                                                        só para buscá-los. */}
                                                    <span>{opLabel(op)}</span>
                                                    {op.amount != null && <span className="text-gray-500 dark:text-gray-400 font-normal">| Valor: R$ {fmt(Number(op.amount))}</span>}
                                                    {op.amount != null && <span className="text-gray-500 dark:text-gray-400 font-normal">| Pago: R$ {fmt(Number(op.paid_amount || 0))}</span>}
                                                    <span className="text-gray-500 dark:text-gray-400 font-normal">| Faturas: {list.length}</span>
                                                </div>
                                                <div className="p-2 space-y-1.5 bg-gray-100/70 dark:bg-slate-800/40">
                                                    {list.map(inv => {
                                                        const sel = isSelected(inv);
                                                        /* Título que já tem marcação pendente de
                                                           OUTRO pagamento. Um título só aceita uma
                                                           marcação: sem isso a primeira aprovação
                                                           pagaria a fatura e a segunda acharia
                                                           fatura já paga apontando para outro
                                                           pagamento — o mesmo dinheiro duas vezes.
                                                           O servidor recusa; aqui a linha entra
                                                           desabilitada para o usuário não montar o
                                                           lote todo e perder o trabalho no fim. */
                                                        const chargeTaken = !!inv.hasSignalledPayment;
                                                        return (
                                                            <div key={inv.invoiceId}
                                                                title={chargeTaken
                                                                    ? 'Este título já está marcado para outro pagamento. Finalize ou remova a sinalização daquele pagamento antes.'
                                                                    : undefined}
                                                                aria-disabled={chargeTaken || undefined}
                                                                className={`flex items-center gap-3 px-3 py-2 rounded border ${chargeTaken
                                                                    ? 'bg-gray-100 dark:bg-slate-900/60 border-gray-200 dark:border-slate-700 opacity-60 cursor-not-allowed'
                                                                    : sel ? 'border-blue-300 dark:border-blue-500 bg-[#f0f8ff] dark:bg-slate-700/50 cursor-pointer'
                                                                        : 'bg-white dark:bg-slate-800 border-gray-200 dark:border-slate-700 hover:bg-gray-50 dark:hover:bg-slate-700/30 cursor-pointer'}`}
                                                                onClick={() => { if (!chargeTaken) toggleInvoice(inv); }}>
                                                                <span>{sel ? <FaCheckSquare className="text-blue-600" /> : <FaRegSquare className="text-gray-300" />}</span>
                                                                {invHeader(inv)}
                                                                {chargeTaken && (
                                                                    <span className="ml-auto text-[10px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 whitespace-nowrap">
                                                                        Pagto Sinalizado
                                                                    </span>
                                                                )}
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Footer */}
                    <div className="bg-white dark:bg-slate-800 rounded-lg shadow-sm p-4 flex items-center justify-between">
                        <button onClick={() => setStep(1)} className="px-4 py-2 border rounded-lg dark:border-slate-600">← Voltar</button>
                        <button disabled={!canSubmit || submitting} onClick={submit}
                            title={canSubmit ? '' : 'A soma dos valores deve ser exatamente 100% do valor recebido'}
                            className="px-6 py-2 bg-green-600 text-white rounded-lg disabled:opacity-50 hover:bg-green-700">
                            {submitting ? 'Registrando...'
                                : identifying ? 'Confirmar' : 'Confirmar Pagamento'}
                        </button>
                    </div>
                </div>
            )}
            </div>

            {infoPayment && (
                <PaymentInfoModal payment={infoPayment} onClose={() => setInfoPayment(null)} />
            )}
        </PageForm>
    );
};

export default RegisterPaymentPage;
